import { GraphQlRequestService } from '../../../api/graphql-request-service';
import { EnvironmentService } from '../../../environment/environment-service';
import { EffectiveRoleViewAuthorizationService } from '../authorization/service';
import { HasDependencies, hashString, RoleId } from '../types';
import {
  GenerateSignedUrlQuery,
  ListUploadedClientObjectsQuery,
} from '../types-graphql';
import { handleGQLErrors } from '../utils';

export class DataUploadService implements HasDependencies {
  private isPanalytSuperAdminAndNonProdEnv: boolean;

  constructor(
    readonly graphQlRequestService: GraphQlRequestService,
    readonly authorizationService: EffectiveRoleViewAuthorizationService,
    readonly environmentService: EnvironmentService,
    readonly simulatedRoleId: RoleId | null
  ) {
    this.isPanalytSuperAdminAndNonProdEnv =
      authorizationService.canManageAllDomains() && !environmentService.isProd;
  }

  public getDeps = () => [
    hashString([
      ...this.graphQlRequestService.getDeps(),
      ...this.authorizationService.getDeps(),
      ...this.environmentService.getDeps(),
      this.simulatedRoleId ?? '',
    ]),
  ];

  public objectUpload = async (
    domain: string,
    fileName: string,
    file: any,
    onProgress?: (progress: number) => void
  ): Promise<any> => {
    // Step 1: Get signed URL
    const result = await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.GenerateUploadSignedUrl({
        domain,
        objectName: fileName,
        simulateRole: this.simulatedRoleId,
      })
    );
    const signedUrl = result.generateUploadSignedUrl;

    if (!signedUrl) {
      throw new Error('Failed to generate signed URL for upload');
    }

    // Step 2: Upload directly to GCS with progress tracking
    await this.uploadWithProgress(signedUrl, file, onProgress);

    return { success: true };
  };

  private uploadWithProgress = async (
    signedUrl: string,
    file: any,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        });
      }

      // Handle completion - success or any kind of failure
      xhr.addEventListener('loadend', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(
            new Error(
              `Upload failed${xhr.status ? `: ${xhr.status} ${xhr.statusText}` : ''}`
            )
          );
        }
      });

      // Start the upload
      xhr.open('PUT', signedUrl);
      xhr.setRequestHeader('Content-Type', 'application/octet-stream');
      xhr.send(file);
    });
  };

  public listUploadedClientObjects = async (
    domain: string
  ): Promise<ListUploadedClientObjectsQuery> => {
    return await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.listUploadedClientObjects({
        domain,
        simulateRole: this.simulatedRoleId,
      })
    );
  };

  public generateSignedUrl = async (
    domain: string,
    objectName: string
  ): Promise<GenerateSignedUrlQuery> => {
    return await handleGQLErrors(
      this.graphQlRequestService.graphQlSdk.generateSignedUrl({
        domain,
        objectName,
        simulateRole: this.simulatedRoleId,
      })
    );
  };

  public deleteUploadedObject = async (domain: string, path: string) => {
    return this.graphQlRequestService.graphQlSdk.deleteUploadedFile({
      domain,
      path,
      simulateRole: this.simulatedRoleId,
    });
  };
}
